import { gql, useQuery, useMutation } from '@apollo/client';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  ChannelCorrectiveAction,
  FeedCorrectiveAction,
  VideoCorrectiveAction,
  QueryGetPendingCorrectiveActionsArgs,
  MutationApplyCorrectiveActionArgs,
  MutationGenerateCorrectiveActionsArgs,
  ApplyCorrectiveActionResult,
  GenerateCorrectiveActionsResult
} from '../types/generated/graphql';


// Fragment for ChannelCorrectiveAction
const CHANNEL_CORRECTIVE_ACTION_FRAGMENT = gql`
  fragment ChannelCorrectiveActionInfo on ChannelCorrectiveAction {
    __typename
    id
    profileId
    type
    status
    trigger
    autoApplyEligible
    algorithmVersion
    title
    description
    createdAt
    appliedAt
    expiresAt
    takenAt
    takenBy
    taskId
    kind
    skipUntil
    feedbackTokens {
      notInterested {
        token
        undoToken
      }
      dontRecommendChannel {
        token
        undoToken
      }
    }
    channel {
      id
      name
      thumbnails {
        url
        width
        height
      }
    }
    channelScore {
      authorId
      score
      videoCount
      restricted
      restrictedVideoCount
      rating
      explanation
      watched {
        id
        title
        thumbnails {
          url
          width
          height
        }
      }
      recommended {
        id
        title
        thumbnails {
          url
          width
          height
        }
      }
      videos {
        id
        score
        video {
          id
          title
          thumbnails {
            url
            width
            height
          }
          author {
            id
            name
          }
        }
      }
    }
  }
`;

// Fragment for VideoCorrectiveAction
const VIDEO_CORRECTIVE_ACTION_FRAGMENT = gql`
  fragment VideoCorrectiveActionInfo on VideoCorrectiveAction {
    __typename
    id
    profileId
    type
    status
    trigger
    autoApplyEligible
    algorithmVersion
    title
    description
    createdAt
    appliedAt
    expiresAt
    takenAt
    takenBy
    taskId
    kind
    skipUntil
    feedbackTokens {
      notInterested {
        token
        undoToken
      }
      dontRecommendChannel {
        token
        undoToken
      }
    }
    videoScore
    video {
      id
      title
      thumbnails {
        url
        width
        height
      }
      author {
        id
        name
        thumbnails {
          url
          width
          height
        }
      }
    }
  }
`;

// Fragment for FeedCorrectiveAction (REMOVE_SHORTS today). No
// channel/video target — the row IS the standing policy. Carries
// reapplyCount / lastAppliedAt so the Activity card can show
// "Applied N times" once the parent approves it.
const FEED_CORRECTIVE_ACTION_FRAGMENT = gql`
  fragment FeedCorrectiveActionInfo on FeedCorrectiveAction {
    __typename
    id
    profileId
    type
    status
    trigger
    autoApplyEligible
    algorithmVersion
    title
    description
    createdAt
    appliedAt
    expiresAt
    takenAt
    takenBy
    taskId
    kind
    skipUntil
    tokenStatus
    reapplyCount
    lastAppliedAt
  }
`;

// Query to get pending corrective actions for a profile
const GET_PENDING_CORRECTIVE_ACTIONS = gql`
  ${CHANNEL_CORRECTIVE_ACTION_FRAGMENT}
  ${VIDEO_CORRECTIVE_ACTION_FRAGMENT}
  ${FEED_CORRECTIVE_ACTION_FRAGMENT}
  query GetPendingCorrectiveActions($profileId: String!) {
    getPendingCorrectiveActions(profileId: $profileId) {
      ... on ChannelCorrectiveAction {
        ...ChannelCorrectiveActionInfo
      }
      ... on VideoCorrectiveAction {
        ...VideoCorrectiveActionInfo
      }
      ... on FeedCorrectiveAction {
        ...FeedCorrectiveActionInfo
      }
    }
  }
`;

// Mutation to apply a corrective action
const APPLY_CORRECTIVE_ACTION = gql`
  ${CHANNEL_CORRECTIVE_ACTION_FRAGMENT}
  ${VIDEO_CORRECTIVE_ACTION_FRAGMENT}
  ${FEED_CORRECTIVE_ACTION_FRAGMENT}
  mutation ApplyCorrectiveAction($input: ApplyCorrectiveActionInput!) {
    applyCorrectiveAction(input: $input) {
      success
      message
      action {
        ... on ChannelCorrectiveAction {
          ...ChannelCorrectiveActionInfo
        }
        ... on VideoCorrectiveAction {
          ...VideoCorrectiveActionInfo
        }
        ... on FeedCorrectiveAction {
          ...FeedCorrectiveActionInfo
        }
      }
    }
  }
`;

// Mutation to generate corrective actions
const GENERATE_CORRECTIVE_ACTIONS = gql`
  mutation GenerateCorrectiveActions($input: GenerateCorrectiveActionsInput!) {
    generateCorrectiveActions(input: $input) {
      success
      message
    }
  }
`;

// Hook to get pending corrective actions for a profile
export function useGetPendingCorrectiveActions(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string
) {
  return useQuery<
    { getPendingCorrectiveActions: (ChannelCorrectiveAction | VideoCorrectiveAction | FeedCorrectiveAction)[] },
    QueryGetPendingCorrectiveActionsArgs
  >(GET_PENDING_CORRECTIVE_ACTIONS, {
    variables: { profileId },
    client,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
}

// Hook to apply a corrective action
export function useApplyCorrectiveAction(client: ApolloClient<NormalizedCacheObject>) {
  const [mutateFunction, mutationResult] = useMutation<
    { applyCorrectiveAction: ApplyCorrectiveActionResult },
    MutationApplyCorrectiveActionArgs
  >(APPLY_CORRECTIVE_ACTION, {
    client,
    errorPolicy: 'all',
    refetchQueries: ['GetCorrectiveActionsForProfile', 'GetPendingCorrectiveActions'],
    awaitRefetchQueries: true,
  });

  return [mutateFunction, mutationResult] as const;
}

// Phase 8 mutations + Phase 9 panel data hooks.

const DISMISS_CORRECTIVE_ACTION = gql`
  mutation DismissCorrectiveAction($actionId: ID!) {
    dismissCorrectiveAction(actionId: $actionId) {
      success
      message
    }
  }
`;

const SKIP_CORRECTIVE_ACTION = gql`
  mutation SkipCorrectiveAction($actionId: ID!, $skipDays: Int) {
    skipCorrectiveAction(actionId: $actionId, skipDays: $skipDays) {
      success
      message
    }
  }
`;

const RETRACT_CORRECTIVE_ACTION = gql`
  mutation RetractCorrectiveAction($actionId: ID!) {
    retractCorrectiveAction(actionId: $actionId) {
      success
      partial
      note
      message
    }
  }
`;

const REFETCH_AFTER_MUTATION = [
  'GetPendingCorrectiveActions',
  'GetCorrectiveActionsForProfile',
];

export function useDismissCorrectiveAction(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<
    {dismissCorrectiveAction: ApplyCorrectiveActionResult},
    {actionId: string}
  >(DISMISS_CORRECTIVE_ACTION, {
    client,
    errorPolicy: 'all',
    refetchQueries: REFETCH_AFTER_MUTATION,
    awaitRefetchQueries: true,
  });
}

export function useSkipCorrectiveAction(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<
    {skipCorrectiveAction: ApplyCorrectiveActionResult},
    {actionId: string; skipDays?: number | null}
  >(SKIP_CORRECTIVE_ACTION, {
    client,
    errorPolicy: 'all',
    refetchQueries: REFETCH_AFTER_MUTATION,
    awaitRefetchQueries: true,
  });
}

export function useRetractCorrectiveAction(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<
    {retractCorrectiveAction: {success: boolean; partial: boolean; note: string | null; message: string}},
    {actionId: string}
  >(RETRACT_CORRECTIVE_ACTION, {
    client,
    errorPolicy: 'all',
    refetchQueries: REFETCH_AFTER_MUTATION,
    awaitRefetchQueries: true,
  });
}

// Filtered query for the channel/video detail PendingActionBanner.
// Uses the new getCorrectiveActionsFor resolver added in phase 3.
const GET_CORRECTIVE_ACTIONS_FOR = gql`
  query GetCorrectiveActionsFor(
    $profileId: String!
    $channelId: String
    $videoId: String
    $statuses: [CorrectiveActionStatus!]
  ) {
    getCorrectiveActionsFor(
      profileId: $profileId
      channelId: $channelId
      videoId: $videoId
      statuses: $statuses
    ) {
      ... on ChannelCorrectiveAction {
        id
        kind
        type
        status
        title
        description
        confidence
        confidenceReason
        severity
        tokenStatus
        createdAt
        takenAt
      }
      ... on VideoCorrectiveAction {
        id
        kind
        type
        status
        title
        description
        confidence
        confidenceReason
        severity
        tokenStatus
        createdAt
        takenAt
      }
      ... on FeedCorrectiveAction {
        id
        kind
        type
        status
        title
        description
        confidence
        confidenceReason
        severity
        tokenStatus
        createdAt
        takenAt
        reapplyCount
        lastAppliedAt
      }
    }
  }
`;

export function useGetCorrectiveActionsFor(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string,
  opts: {channelId?: string; videoId?: string; statuses?: string[]} = {},
) {
  return useQuery<
    {getCorrectiveActionsFor: any[]},
    {profileId: string; channelId?: string; videoId?: string; statuses?: string[]}
  >(GET_CORRECTIVE_ACTIONS_FOR, {
    client,
    variables: {profileId, ...opts},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
}

// Admin-only mutation to force a re-eval of every corrective action
// for a profile. Marks the per-profile video evaluator + every
// (profile, channel) pair the parent has feedback on as dirty; the
// 5-min scheduler tick picks them up. See `admin.graphql`.
const ADMIN_RECOMPUTE_CORRECTIVE_ACTIONS = gql`
  mutation AdminRecomputeCorrectiveActions($profileId: ID!) {
    adminRecomputeCorrectiveActions(profileId: $profileId) {
      profileId
      videoMarked
      channelsMarked
      message
    }
  }
`;

export function useAdminRecomputeCorrectiveActions(
  client: ApolloClient<NormalizedCacheObject>,
) {
  return useMutation<
    {
      adminRecomputeCorrectiveActions: {
        profileId: string;
        videoMarked: boolean;
        channelsMarked: number;
        message: string;
      };
    },
    {profileId: string}
  >(ADMIN_RECOMPUTE_CORRECTIVE_ACTIONS, {
    client,
    errorPolicy: 'all',
    refetchQueries: REFETCH_AFTER_MUTATION,
  });
}

// Hook to generate corrective actions
export function useGenerateCorrectiveActions(client: ApolloClient<NormalizedCacheObject>) {
  const [mutateFunction, mutationResult] = useMutation<
    { generateCorrectiveActions: GenerateCorrectiveActionsResult },
    MutationGenerateCorrectiveActionsArgs
  >(GENERATE_CORRECTIVE_ACTIONS, {
    client,
    errorPolicy: 'all',
    refetchQueries: ['GetCorrectiveActionsForProfile', 'GetPendingCorrectiveActions'],
    awaitRefetchQueries: true,
  });

  return [mutateFunction, mutationResult] as const;
}